import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { ListResult, ProductionOrderListItem, StatusView } from "@cx/contracts";
import { formatDateBR, formatQty } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";

export type ProductionFormOptions = {
  warehouses: { id: string; name: string; isDefault: boolean }[];
  statuses: StatusView[];
};

/** Suprimentos › Ordens de produção (RF-SUP-05): busca por número ou produto, filtro por situação; produzido x total. */
export function ProductionOrdersListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statusId, setStatusId] = useState("");
  const term = q.trim();
  const canCreate = can(perms, "production_orders", "create");
  const options = useQuery({
    queryKey: ["production-orders", companyId, "form-options"],
    queryFn: () => api<ProductionFormOptions>("/production-orders/form-options"),
  });
  const list = useInfiniteQuery({
    queryKey: ["production-orders", companyId, "list", term, statusId],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ListResult<ProductionOrderListItem>>(
        `/production-orders?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(statusId ? { statusId } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const count = list.data?.pages[0]?.summary.count;
  const create = () => navigate({ to: "/suprimentos/ordens-de-producao/novo" });

  return (
    <>
      <ContentHeader
        title="Ordens de produção"
        breadcrumb={["Suprimentos", "Ordens de produção"]}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={create}>
              Incluir ordem de produção
            </button>
          )
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-5">
            <label className="visually-hidden" htmlFor="op-q">
              Pesquisar ordem de produção
            </label>
            <input
              id="op-q"
              type="search"
              className="form-control"
              placeholder="Número, produto ou SKU"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {options.data && (
            <div className="col-md-3">
              <label className="visually-hidden" htmlFor="op-status">
                Situação
              </label>
              <select
                id="op-status"
                className="form-select"
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <option value="">Todas as situações</option>
                {options.data.statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma ordem de produção"
            description="Cadastre a estrutura do produto (aba Estrutura) e abra uma ordem: cada apontamento baixa os insumos e dá entrada no produto com o custo apurado."
            actions={canCreate ? [{ label: "Incluir ordem de produção", onClick: create }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div
              className="table-responsive"
              tabIndex={0}
              role="region"
              aria-label="Ordens de produção (rolagem)"
            >
              <table className="table align-middle mb-0" aria-label="Ordens de produção">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Produto</th>
                    <th scope="col" className="text-end">
                      Quantidade
                    </th>
                    <th scope="col" className="text-end">
                      Produzido
                    </th>
                    <th scope="col">Previsão de término</th>
                    <th scope="col">Responsável</th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to="/suprimentos/ordens-de-producao/$id" params={{ id: r.id }}>
                          {r.number}
                        </Link>
                      </td>
                      <td>
                        {r.productName}
                        {r.productSku && <span className="text-muted small ms-2">{r.productSku}</span>}
                      </td>
                      <td className="text-end qty">
                        {formatQty(r.quantityQ4)} {r.unit}
                      </td>
                      <td className="text-end qty">{formatQty(r.producedQ4)}</td>
                      <td>{r.plannedEnd ? formatDateBR(r.plannedEnd) : "—"}</td>
                      <td>{r.responsible ?? "—"}</td>
                      <td>
                        <StatusBadge tone={r.status.color}>{r.status.label}</StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {count !== undefined && count >= 0 && (
                <span className="text-muted small" data-testid="op-summary">
                  {count} {count === 1 ? "ordem" : "ordens"}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
